import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import RuleFolderIcon from '@mui/icons-material/RuleFolderOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHorizOutlined';
import { bulkAssignLeads, listLeads } from '../../api/leads';
import { listLookup } from '../../api/masterData';
import { LeadListItem } from '../../types/lead';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';
import { useBranchFilter } from '../../context/branchContext';
import { BulkAssignDialog } from './BulkAssignDialog';
import { AssignmentHistoryTable } from './AssignmentHistoryTable';
import { TransferCounselorLeadsDialog } from './TransferCounselorLeadsDialog';

/**
 * Central Lead Distribution: a dedicated workflow, not a relabeled Leads
 * page. Shows the unassigned pool (assignedTo is null) with its own
 * search/filter/pagination/selection, an individual-or-bulk "Assign to
 * Counselor" action (BulkAssignDialog handles both 1 and many leads —
 * same optional-reason flow either way, so there's no separate dialog to
 * keep in sync), and the pool-wide assignment history section.
 */
export const CentralLeadDistributionPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Same header branch selector already wired into the Leads page — for
  // globally-scoped roles (Super Admin, Lead Manager) this narrows the
  // central pool to one branch; inert (always undefined) for anyone else,
  // since they're already branch-scoped server-side regardless.
  const { selectedBranchId } = useBranchFilter();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [search, setSearch] = useState('');
  const [statusId, setStatusId] = useState<number | ''>('');
  const [sourceId, setSourceId] = useState<number | ''>('');
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);

  const filters = {
    search: search || undefined,
    leadStatusId: statusId || undefined,
    leadSourceId: sourceId || undefined,
    branchId: selectedBranchId,
    unassigned: true,
  };

  const unassignedQuery = useQuery({
    queryKey: ['leads', 'unassigned-pool', paginationModel.page, paginationModel.pageSize, selectedBranchId, filters],
    queryFn: () => listLeads({ page: paginationModel.page + 1, pageSize: paginationModel.pageSize, ...filters }),
    placeholderData: (prev) => prev,
  });

  const statusesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-statuses'],
    queryFn: () => listLookup('/master-data/lead-statuses'),
  });
  const sourcesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-sources'],
    queryFn: () => listLookup('/master-data/lead-sources'),
  });

  const invalidatePool = () => {
    queryClient.invalidateQueries({ queryKey: ['leads', 'unassigned-pool'] });
    queryClient.invalidateQueries({ queryKey: ['lead-assignment-history'] });
  };

  const assignMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: number; reason?: string }) =>
      bulkAssignLeads(selection as number[], userId, reason),
    onSuccess: (result) => {
      invalidatePool();
      setAssignOpen(false);
      setAssignError(null);
      setResultMessage(`Assigned ${result.updated} lead(s) successfully.`);
      setSelection([]);
    },
    onError: (err) => setAssignError(extractErrorMessage(err)),
  });

  const columns: GridColDef<LeadListItem>[] = useMemo(
    () => [
      { field: 'leadNumber', headerName: 'Lead #', width: 110 },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        valueGetter: (_v, row) => `${row.firstName} ${row.lastName}`,
      },
      { field: 'phone', headerName: 'Phone', width: 140 },
      {
        field: 'leadSource',
        headerName: 'Source',
        width: 130,
        valueGetter: (_v, row) => row.leadSource.name,
      },
      {
        field: 'leadStatus',
        headerName: 'Status',
        width: 150,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.leadStatus.name}
            sx={{ bgcolor: params.row.leadStatus.colorHex, color: '#fff' }}
          />
        ),
      },
      {
        field: 'branch',
        headerName: 'Branch',
        width: 130,
        valueGetter: (_v, row) => row.branch?.name ?? '—',
      },
    ],
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={RuleFolderIcon}
        color="#B5451B"
        title="Central Lead Distribution"
        subtitle="Assign unassigned leads from the central pool to a counselor."
        actions={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<SwapHorizIcon />} onClick={() => setTransferOpen(true)}>
              Transfer Leads
            </Button>
            {selection.length > 0 && (
              <Button
                variant="contained"
                startIcon={<GroupIcon />}
                onClick={() => {
                  setAssignError(null);
                  setAssignOpen(true);
                }}
              >
                {selection.length === 1 ? 'Assign lead' : `Assign (${selection.length})`}
              </Button>
            )}
          </Stack>
        }
      />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Chip
          color="warning"
          label={`Unassigned leads: ${unassignedQuery.data?.meta.total ?? '—'}`}
          sx={{ fontWeight: 600 }}
        />
        <Button size="small" onClick={() => navigate('/leads')}>
          View all leads
        </Button>
      </Stack>

      {resultMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResultMessage(null)}>
          {resultMessage}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          placeholder="Search name, phone, email, lead #…"
          size="small"
          sx={{ width: 300 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          sx={{ width: 180 }}
          value={statusId}
          onChange={(e) => setStatusId(e.target.value ? Number(e.target.value) : '')}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {statusesQuery.data?.map((s) => (
            <MenuItem key={s.id as number} value={s.id as number}>
              {String(s.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Source"
          sx={{ width: 180 }}
          value={sourceId}
          onChange={(e) => setSourceId(e.target.value ? Number(e.target.value) : '')}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {sourcesQuery.data?.map((s) => (
            <MenuItem key={s.id as number} value={s.id as number}>
              {String(s.name)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ height: 480, bgcolor: 'background.paper', borderRadius: 1, mb: 4 }}>
        <DataGrid
          rows={unassignedQuery.data?.data ?? []}
          columns={columns}
          rowCount={unassignedQuery.data?.meta.total ?? 0}
          loading={unassignedQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          slots={{ toolbar: TableToolbar }}
          checkboxSelection
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
        />
      </Box>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Assignment history
      </Typography>
      <AssignmentHistoryTable />

      <BulkAssignDialog
        open={assignOpen}
        leadCount={selection.length}
        errorMessage={assignError}
        onClose={() => setAssignOpen(false)}
        onSubmit={async (userId, reason) => {
          await assignMutation.mutateAsync({ userId, reason });
        }}
      />

      <TransferCounselorLeadsDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        onTransferred={() => {
          setTransferOpen(false);
          invalidatePool();
          setResultMessage('Leads transferred successfully.');
        }}
      />
    </Box>
  );
};

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};
